import React, { useEffect, useState } from "react";
import { LoadingBox, TextInput, ViewCard } from "../../components";
import { useParams } from "react-router-dom";
import PDFViewer from "../../utils/PdfViewer";
import {
  useGetSingleJobQuery,
  useUpdateJobMutation,
} from "../../store/api/jobs";
import { Button, Card, Col, Form, Modal, Row } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import { FaEye } from "react-icons/fa";
import Stepper from "../../components/stepper/Stepper";
import { toast, ToastContainer } from "react-toastify";
import { toastOptions } from "../../utils/error";

const keyProps = {
  "Intended User": "intended_user",
  Description: "description",
  "Intended Username": "intended_username",
  "Job Status": "job_status",
  "Property Type": "property_type",
  "Preferred Date": "preferred_date",
  Phone: "phone",
  "Country Code": "country_code",
  Purpose: "purpose",
  Use: "use",
  Address: "address",
  "Rush Request": "is_rush_req",
  Price: "price",
  Status: "status",
  "Property Rights": "property_rights",
  Occupant: "occupant",
  Comments: "comments",
  Notes: "notes",
  "Estimate Market Value": "estimate_market_value",
  "Effective Date": "effective_date",
  "Expires At": "expires_at",
};

const getCurrentStep = (status) => {
  switch (status) {
    case "active":
      return 1;
    case "client_visit":
      return 2;
    case "site_visit_scheduled":
      return 2;
    case "post_visit_summary":
      return 3;
    case "completed":
      return 4;
  }
};

const steps = [
  { label: "Job Request", step: 1 },
  { label: "Job Tracking", step: 2 },
  { label: "Report Review", step: 3 },
];

const EditModal = ({ show, onHide, data, fetchJob }) => {
  // console.log(data);
  const { id } = useParams();

  const [formData, setFormData] = useState({});

  const [updateJob, { isLoading, isSuccess, isError, error }] =
    useUpdateJobMutation();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await updateJob({
        id,
        body: {
          ...formData,
          is_rush_req: !formData.is_rush_req
            ? String(formData.is_rush_req)
            : formData.is_rush_req,
        },
      });
      if (response && response.data) {
        onHide();
        fetchJob();
        toast.success(response.data?.message, toastOptions);
      }
      if (response && response.error) {
        toast.error(response.error?.data?.message, toastOptions);
      }
    } catch (error) {
      console.log(error);
      toast.error(error.message);
    }
  };

  useEffect(() => {
    // Dynamically set form data when `data` changes
    if (data) {
      setFormData({
        is_rush_req: data.is_rush_req || false,
        notes: data.notes || "",
        comments: data.comments || "",
        job_status: data.job_status || "",
        estimate_market_value: data.estimate_market_value || "",
      });
    }
  }, [data]);

  return (
    <>
      <Modal show={show} onHide={onHide}>
        <Modal.Header>Edit Job</Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            {/* Comment Field */}
            <TextInput
              label="Comment"
              type="text"
              name="comments"
              value={formData.comments}
              onChange={handleChange}
              placeholder="Enter comment"
            />

            {/* Status Field */}
            <Form.Group className="mb-3">
              <Form.Label>Job Status</Form.Label>
              <Form.Control
                as="select"
                name="job_status"
                value={formData.job_status}
                onChange={handleChange}
              >
                <option value="">Select Job Status</option>
                <option value="active">Active</option>
                <option value="client_visit">Client Visit</option>
                <option value="post_visit_summary">Post Visit Summary</option>
                <option value="site_visit_scheduled">
                  Site Visit Scheduled
                </option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </Form.Control>
            </Form.Group>

            {/* Rush Request Field */}
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Rush Request"
                name="is_rush_req"
                checked={formData.is_rush_req}
                onChange={handleChange}
              />
            </Form.Group>

            {/* Notes Field */}
            <Form.Group controlId="notes" className="mt-3 mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                name="notes"
                rows={3}
                value={formData.notes}
                onChange={handleChange}
                placeholder="Enter notes"
              />
            </Form.Group>

            {/* Estimated Market Value Field */}
            <TextInput
              label="Estimate Market Value"
              type="text"
              name="estimate_market_value"
              value={formData.estimate_market_value}
              onChange={handleChange}
              placeholder="Enter estimated market value"
            />

            <div className="d-flex justify-content-end mt-4">
              <Button variant="secondary" onClick={onHide} className="me-2">
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Save Changes{" "}
                {isLoading && (
                  <LoadingBox style={{ height: "1rem", width: "1rem" }} />
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      <ToastContainer />
    </>
  );
};

const ViewJob = () => {
  const { id } = useParams();
  const [showModal, setModalShow] = useState(false);

  const { isLoading, isFetching, isSuccess, isError, error, data, refetch } =
    useGetSingleJobQuery(id);

  return (
    <>
      <ViewCard
        data={data?.job && { ...data?.job }}
        keyProps={keyProps}
        title="Job Details"
        isImage={false}
        reducerProps={{ isLoading, error, success: isSuccess, isFetching }}
        setModalShow={setModalShow}
        showEdit={true}
      >
        {data?.job && (
          <>
            <PDFViewer
              title={"Document By Lender"}
              pdfUrl={data?.job.lender_doc}
            />
          </>
        )}
        <Card.Body className="my-3">
          <Row className="mb-4">
            <Card.Header className="d-flex align-items-center">
              <Card.Title>Job Summary</Card.Title>
            </Card.Header>
            <Stepper
              steps={steps}
              currentStep={getCurrentStep(data?.job?.job_status)}
            />
          </Row>
          <Row className="mb-3">
            <Card.Header className="d-flex align-items-center">
              <Card.Title>Document Verification</Card.Title>
            </Card.Header>
          </Row>
          <Row className="mb-4">
            <Col md={6} lg={4} className="">
              <p className="mb-1">
                <strong>Uploaded Document</strong>
              </p>
              <p>
                {isLoading ? (
                  <Skeleton />
                ) : (
                  <div className="">
                    {data?.job.lender_doc ? (
                      <>
                        <iframe
                          src={data?.job?.lender_doc}
                          width="100%"
                          title="pdf"
                        />
                        <div className="d-flex justify-content-center">
                          <a
                            href={data?.job.lender_doc}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <button className="btn btn-primary">
                              <FaEye size={20} />
                            </button>
                          </a>
                        </div>
                      </>
                    ) : (
                      <span>Document is not provided</span>
                    )}
                  </div>
                )}
              </p>
            </Col>
          </Row>

          {isLoading ? (
            <Skeleton />
          ) : (
            data?.job?.appraiser_docs?.length && (
              <>
                <Row className="mb-3">
                  <Card.Header className="d-flex align-items-center">
                    <Card.Title>Appraiser Documents</Card.Title>
                  </Card.Header>
                </Row>

                <Row>
                  {data?.job?.appraiser_docs.map((item) => (
                    <Col md={6} lg={4}>
                      <div>
                        {isLoading ? (
                          <Skeleton />
                        ) : (
                          <div className="">
                            {data?.job.lender_doc ? (
                              <>
                                <iframe src={item} width="100%" title="pdf" />
                                <div className="d-flex justify-content-center">
                                  <a
                                    href={item}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <button className="btn btn-primary">
                                      <FaEye size={20} />
                                    </button>
                                  </a>
                                </div>
                              </>
                            ) : (
                              <span>Document is not provided</span>
                            )}
                          </div>
                        )}
                      </div>
                    </Col>
                  ))}
                </Row>
              </>
            )
          )}
        </Card.Body>
        <EditModal
          show={showModal}
          onHide={() => setModalShow(false)}
          data={data?.job}
          fetchJob={refetch}
        />
      </ViewCard>
      <ToastContainer />
    </>
  );
};

export default ViewJob;
